/**
 * The home's composer: the shared `MessageInput` with the controls that act
 * before a session exists (file upload, add context, Skills, reasoning effort,
 * mic, send), and the canvas landing's quick actions under it. From lg, Send
 * and a quick action both run the dispatcher. Below lg, both start a chat
 * instead, the way the legacy home's Send does, since the canvas is not built
 * for phones; a quick action's chat carries a hidden addendum asking for its
 * board as one HTML file record.
 *
 * Attachments upload into the private workspace, where the canvas or the chat
 * lands too, and their ids ride the first message. A file dropped anywhere on
 * the home attaches here (the layout's OS drop layer), as do in-app drags onto
 * the input; Send waits for uploads the way the legacy home's does.
 */

import type { MessageInputMethod } from "@/analytics/surfaces";
import { usePrivateWorkspace } from "@/api/workspaces";
import { ComposerSuggestions } from "@/components/ComposerSuggestions";
import { MessageInput } from "@/components/MessageInput";
import { useHarnessControl } from "@/components/MessageInput/hooks/useHarnessControl";
import { usePermissionModeControl } from "@/components/MessageInput/hooks/usePermissionModeControl";
import {
  sendSettings,
  sentSettings,
} from "@/components/MessageInput/sendSettings";
import { MessageInputControlSet } from "@/constants/message-input";
import { useSessionContext } from "@/context/SessionContext";
import { ComposerAttachDropTarget } from "@/dnd/ComposerAttachDropTarget";
import { useRegisterOsFileDrop } from "@/dnd/OsFileDropLayer";
import { useRegisterComposerAttachTarget } from "@/dnd/TransferDndProvider";
import { useBreakpoint } from "@/hooks/useBreakpoint";
import type { UploadTarget } from "@/hooks/useFileRecordUpload";
import { useHarnessDraft } from "@/hooks/useHarnessDraft";
import { useMessageActions } from "@/hooks/useMessageActions";
import { useOpenChat } from "@/hooks/useOpenChat";
import { useReasoningEffort } from "@/pages/canvas/hooks/useCanvasReasoningEffort";
import {
  PERMISSION_MODES,
  type PermissionMode,
  type PostMessageParams,
  type ReferencedEntity,
} from "@cohesive/api-client";
import { useCallback, useMemo, useState } from "react";
import toast from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import type { HomeSend } from "./dispatch";
import {
  homeHeaderPlacement,
  MOBILE_QUICK_ACTION_ADDENDUM,
  pickQuickActions,
} from "./quickActions";

export interface NewHomeComposerProps {
  onSend: (message: HomeSend) => void;
  busy: boolean;
}

export const CHAT_FAILED = "Failed to create chat";

export function NewHomeComposer({ onSend, busy }: NewHomeComposerProps) {
  // The canvas lands in the private workspace; until it resolves there is
  // nowhere to create one or to upload into, so Send and attach stay off.
  const privateWorkspace = usePrivateWorkspace();
  const lg = useBreakpoint("lg");
  const [referencedEntities, setReferencedEntities] = useState<
    ReferencedEntity[]
  >([]);
  // The same key as the legacy home composer, so the preference carries over; the quick actions
  // send the same.
  const [reasoningEffort, setReasoningEffort] = useReasoningEffort(
    "composer_reasoning_effort_home",
  );
  // The same key as the legacy home composer and its floating chat, so all three start chats on
  // one agent.
  const [harnessDraft, setHarnessDraft, harnessCreateFields, harnessReady] =
    useHarnessDraft("composer_harness_home");
  // Not persisted: allow-all must never carry into a chat the user did not pick it for.
  const [confirmationMode, setConfirmationMode] = useState<PermissionMode>(
    PERMISSION_MODES.AUTO,
  );
  // The quick actions send what the composer's own Send would: these controls read the same draft.
  const harnessControl = useHarnessControl({
    draft: harnessDraft,
    onDraftChange: setHarnessDraft,
    hostTier: reasoningEffort,
  });
  const permissionModeControl = usePermissionModeControl({
    draftMode: confirmationMode,
    onDraftModeChange: setConfirmationMode,
  });
  // Three of the six, drawn once per visit.
  const [quickActions] = useState(() => pickQuickActions());

  const attachTarget = useMemo<UploadTarget | undefined>(
    () =>
      privateWorkspace
        ? { class: "workspace", workspace_id: privateWorkspace.id }
        : undefined,
    [privateWorkspace],
  );
  const { openChat } = useOpenChat();
  const { setCurrentSessionId } = useSessionContext();
  const {
    uploadedFiles,
    readyFileRecordIds,
    onAttachFile,
    onAttachFileRecords,
    onRemoveFile,
    onSendMessage,
    isLoading,
  } = useMessageActions({
    surface: "home_composer",
    attachTarget,
    harnessSelection: harnessCreateFields,
    harnessReady,
    onMessageSent: ({ flowSession }) => {
      openChat(flowSession.id, { mode: "replace", source: "hero-composer" });
      // Reset so the composer starts a fresh chat if the user comes back.
      setCurrentSessionId(undefined);
      // …and that fresh chat starts at Auto.
      setConfirmationMode(PERMISSION_MODES.AUTO);
    },
  });
  const attach = attachTarget ? onAttachFile : undefined;

  const onOsFileDrop = useCallback(
    (files: File[] | null) => attach?.(files, { method: "drop_os" }),
    [attach],
  );
  useRegisterOsFileDrop(attach && onOsFileDrop);
  // No session to copy into before the send: in-app drags attach workspace
  // files as references.
  useRegisterComposerAttachTarget("home", {
    ensureSession: async () => null,
    onAttached: (records) =>
      onAttachFileRecords(records, { method: "drop_in_app" }),
  });

  const handleSend = async (
    params: PostMessageParams,
    meta?: { inputMethod: MessageInputMethod },
  ) => {
    const content = params.content?.trim() ?? "";
    if (!content && readyFileRecordIds.length === 0) return;
    if (!harnessReady) return;
    if (!lg) {
      // The ready file ids merge inside useMessageActions. A rejected send
      // keeps the prompt in the editor (MessageInput clears it only after
      // the await), so toast and let it through.
      try {
        await onSendMessage(
          {
            id: uuidv4(),
            content,
            referenced_entities: referencedEntities,
            ...sentSettings(params),
          },
          meta,
        );
      } catch (error) {
        toast.error(CHAT_FAILED);
        throw error;
      }
      return;
    }
    onSend({
      content,
      referenced_entities: referencedEntities,
      ...sentSettings(params),
      agent: harnessCreateFields,
      ...(readyFileRecordIds.length
        ? { file_record_ids: readyFileRecordIds }
        : {}),
    });
  };

  return (
    <div className="flex w-full flex-col gap-4">
      <ComposerAttachDropTarget scopeKey="home" className="w-full rounded-xl">
        <div
          className="w-full rounded-xl"
          data-onboarding-target="home-composer"
        >
          <MessageInput
            attachedFiles={uploadedFiles}
            onAttachFile={attach}
            onAttachFileRecords={onAttachFileRecords}
            onRemoveFile={onRemoveFile}
            filesWorkspaceId={privateWorkspace?.id}
            controlSet={MessageInputControlSet.NEW_HOME}
            agentLineMenuSide="bottom"
            onSend={handleSend}
            onReferencedEntitiesChange={setReferencedEntities}
            sendButtonDisabled={busy || isLoading || !privateWorkspace}
            autoFocus={lg}
            location="main"
            agentReasoningEffort={reasoningEffort}
            onAgentReasoningEffortChange={setReasoningEffort}
            harnessDraft={harnessDraft}
            onHarnessDraftChange={setHarnessDraft}
            confirmationMode={confirmationMode}
            onConfirmationModeChange={setConfirmationMode}
            classNames={{ container: "w-full", editor: "min-h-[72px]" }}
          />
        </div>
      </ComposerAttachDropTarget>
      <ComposerSuggestions
        suggestions={quickActions}
        className="grid-cols-1 sm:grid-cols-2 lg:grid-cols-3"
        // Three in a row from lg; two where three do not fit, never an
        // orphan; phones stack them.
        cardClassName={(index) => (index === 2 ? "sm:max-lg:hidden" : "")}
        // Held like Send until the picked subscription is connected.
        disabled={
          busy ||
          isLoading ||
          !privateWorkspace ||
          !harnessReady ||
          !harnessControl.canSend
        }
        onSelect={(suggestion) => {
          if (!harnessReady) return;
          const quickActionSettings = sendSettings(
            harnessControl,
            permissionModeControl,
          );
          if (!lg) {
            void onSendMessage(
              {
                id: uuidv4(),
                content: suggestion.prompt,
                referenced_entities: [],
                ...quickActionSettings,
                addendum: MOBILE_QUICK_ACTION_ADDENDUM,
              },
              { inputMethod: "suggested_action", withoutAttachments: true },
            ).catch(() => toast.error(CHAT_FAILED));
            return;
          }
          onSend({
            content: suggestion.prompt,
            referenced_entities: [],
            ...quickActionSettings,
            agent: harnessCreateFields,
            header: homeHeaderPlacement(suggestion.id),
          });
        }}
      />
    </div>
  );
}
